import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { PosMappingEntity } from "src/app/features/PosMapping/types/PosMappingEntity";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayEnumValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const PosMappingsTable: FC<ResourceTableProps<PosMappingEntity>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<PosMappingEntity>): ExtendedColumnsType<PosMappingEntity>[] => {
            return [
                {
                    title: t("posMapping.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("posMapping.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("posMapping.posSystem"),
                    key: "posSystem",
                    dataIndex: "posSystem",
                    sorter: () => 0,
                    render: (value) => displayEnumValue(value, "pos-mapping-systems"),
                    sortOrder: sortInfo.columnKey === "posSystem" ? sortInfo.order : null,
                },
                {
                    title: t("posMapping.posVersion"),
                    key: "posVersion",
                    dataIndex: "posVersion",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "posVersion" ? sortInfo.order : null,
                },
                {
                    title: t("posMapping.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("posMapping.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    render: (value) => displayDateTimeValue(value),
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: PosMappingEntity) => navigate(`${BASE_PATH}/${ResourceName.POS_MAPPINGS}/${FORM_ACTION.EDIT}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<PosMappingEntity>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="posMappings_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: PosMappingEntity) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
